const DEFAULT_PAGE_TEST_TIMEOUT_MS = 30000;

export class IframeTestRunner {
	constructor({ ruleRunner, layout, normalizePostId }) {
		this.ruleRunner = ruleRunner;
		this.layout = layout;
		this.normalizePostId = normalizePostId;
	}

	createFailedPage(page, errorMessage) {
		const failedPage = this.ruleRunner.createDefaultPage(page);

		for (const rule of this.ruleRunner.rules) {
			failedPage.rules[rule.key] = 'skipped';
			failedPage.ruleDetails[rule.key] = {
				error: errorMessage
			};
		}

		failedPage.failedSelectorsByRule = this.ruleRunner.buildDefaultFailedSelectorsMap();
		failedPage.rulePartsByRule = this.ruleRunner.buildDefaultRulePartsMap();

		return failedPage;
	}

	async runPageWithTimeout(page, timeoutMs) {
		let timeoutId = 0;
		const timeoutPromise = new Promise((_, reject) => {
			timeoutId = window.setTimeout(() => {
				reject(new Error('iframe_test_timeout'));
			}, timeoutMs);
		});

		const pagePromise = (async () => {
			const loadResult = await this.layout.loadIframeUrl(page.url);
			const normalizedPostId = this.normalizePostId(loadResult.postId ?? page.postId);
			const pageWithPostId = {
				...page,
				postId: normalizedPostId
			};

			const updatedPage = await this.ruleRunner.runOnDocument(pageWithPostId, loadResult.documentNode);

			return {
				loadResult,
				normalizedPostId,
				updatedPage
			};
		})();

		try {
			return await Promise.race([pagePromise, timeoutPromise]);
		} finally {
			if (timeoutId) {
				window.clearTimeout(timeoutId);
			}
		}
	}

	async run(crawledPages, hooks = {}) {
		const totalPages = crawledPages.length;
		const pagesToCheck = this.ruleRunner.normalizePages(crawledPages);
		const pages = [];
		const pageTimeoutMs = Number.isFinite(hooks.pageTimeoutMs) && hooks.pageTimeoutMs > 0
			? hooks.pageTimeoutMs
			: DEFAULT_PAGE_TEST_TIMEOUT_MS;
		let timeoutCount = 0;
		let warningCount = 0;

		for (const [pageIndex, page] of pagesToCheck.entries()) {
			if (hooks.shouldStop?.()) {
				break;
			}

			pages[pageIndex] = this.ruleRunner.createDefaultPage(page);
			hooks.onPageInitialize?.({ pageIndex, totalPages, page: pages[pageIndex], pages });

			try {
				const { loadResult, normalizedPostId, updatedPage } = await this.runPageWithTimeout(page, pageTimeoutMs);

				if (normalizedPostId) {
					hooks.onPagePostId?.({ pageUrl: page.url, postId: normalizedPostId });
				}

				if (hooks.shouldStop?.()) {
					break;
				}

				pages[pageIndex] = updatedPage;
				hooks.onPageComplete?.({ pageIndex, page: updatedPage, pages, documentNode: loadResult.documentNode });

				if (!loadResult.loaded) {
					warningCount += 1;
					hooks.onPageWarning?.({
						pageUrl: page.url,
						reason: 'load_failed'
					});
				}
			} catch (error) {
				if (!(error instanceof Error) || error.message !== 'iframe_test_timeout') {
					throw error;
				}

				const timeoutMessage = `Page test timed out after ${Math.round(pageTimeoutMs / 1000)} seconds`;
				const timedOutPage = this.createFailedPage(page, timeoutMessage);
				pages[pageIndex] = timedOutPage;
				timeoutCount += 1;
				warningCount += 1;
				hooks.onPageComplete?.({ pageIndex, page: timedOutPage, pages, documentNode: null });
				hooks.onPageWarning?.({
					pageUrl: page.url,
					reason: 'timeout',
					timeoutMs: pageTimeoutMs
				});
			}
		}

		return {
			pages,
			totalPages,
			stopped: hooks.shouldStop?.() || false,
			timeoutCount,
			warningCount
		};
	}
}
